/**
 * WorkspaceShortcutsPanel
 * section prop: 'timestamps'
 *
 * 'timestamps' → renders in ws-col-timestamps (scrollable card)
 */
import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck, faRotateRight, faLock, faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { generateShortcuts } from './generateShortcuts';

/* ── Skeleton helpers ─────────────────────────────────────────── */

const SkeletonTimestamps = () => (
  <div className="ws-skeleton-block">
    {[['32px', '55%'], ['32px', '70%'], ['32px', '45%'], ['32px', '65%'],
      ['32px', '50%'], ['32px', '60%'], ['32px', '40%']].map(([tw, lw], i) => (
      <div key={i} className="ws-skeleton-ts-row">
        <div className="ws-skeleton-ts-time" style={{ width: tw }} />
        <div className="ws-skeleton-ts-text" style={{ flex: 1, maxWidth: lw }} />
      </div>
    ))}
  </div>
);

/* ── Main component ───────────────────────────────────────────── */

const WorkspaceShortcutsPanel = ({
  section,               // 'timestamps'
  isTranscriptAvailable,
  isYTProcessing,
  isLOCALProcessing,
  isScRun,
  isScVideoPrivate,
  timestamps,
  content_id,
  previewLocked = false, // a free upload preview: only the first chapters are here until Pro
  previewHidden = 0,
  isPro = false,
  isUnlocking = false,
  onUnlock,
}) => {
  const [copiedTs, setCopiedTs]   = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const cooldownRef = useRef(null);

  const hasTimestamps = typeof timestamps === 'string' && timestamps.trim() !== '';

  const copy = (text, setter) => {
    navigator.clipboard.writeText(text).then(() => {
      setter(true);
      setTimeout(() => setter(false), 3000);
    });
  };

  const handleReload = async () => {
    if (isReloading) return;
    setIsReloading(true);
    try { await generateShortcuts(content_id, true); } catch { /* silent */ }
    cooldownRef.current = setTimeout(() => setIsReloading(false), 120000);
  };


  /* ── TIMESTAMPS ─────────────────────────────────────────── */
  if (section === 'timestamps') {
    return (
      <>
        <div className="ws-section-header">
          <span className="ws-section-label">Timestamps</span>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {isScRun && !isScVideoPrivate && !hasTimestamps && (
              <RetryBtn onClick={handleReload} loading={isReloading} />
            )}
            {hasTimestamps && (
              <button className="ws-copy-btn" onClick={() => copy(timestamps, setCopiedTs)} aria-label="Copy timestamps" title="Copy timestamps">
                <FontAwesomeIcon icon={copiedTs ? faCheck : faCopy} className={`ws-copy-icon${copiedTs ? ' is-copied' : ''}`} />
              </button>
            )}
          </div>
        </div>

        {/* Private/unlisted video banner */}
        {isScVideoPrivate && (
          <PrivateVideoBanner onRetry={handleReload} loading={isReloading} />
        )}

        {/* Not uploaded */}
        {!isScVideoPrivate && !isTranscriptAvailable && (
          <div className="ws-state-center">
            <p className="ws-state-text">{(isYTProcessing || isLOCALProcessing) ? 'Timestamps will be ready once your video finishes processing.' : 'Upload a video to generate timestamps.'}</p>
          </div>
        )}

        {/* Generating — skeleton */}
        {!isScVideoPrivate && isTranscriptAvailable && !isScRun && <SkeletonTimestamps />}

        {/* Done */}
        {!isScVideoPrivate && isScRun && (
          hasTimestamps ? (
            <div className="ws-ts-list">
              {timestamps.split('\n').filter(Boolean).map((line, i) => {
                const idx = line.indexOf(' - ');
                if (idx === -1) return (
                  <div key={i} className="ws-ts-row"><span className="ws-ts-text">{line}</span></div>
                );
                return (
                  <div key={i} className="ws-ts-row">
                    <span className="ws-ts-time">{line.slice(0, idx)}</span>
                    <span className="ws-ts-sep">—</span>
                    <span className="ws-ts-text">{line.slice(idx + 3)}</span>
                  </div>
                );
              })}
              {previewLocked && (
                isPro || isUnlocking ? (
                  <div className="ws-preview-unlocking">
                    <FontAwesomeIcon icon={faCircleNotch} spin /> Unlocking all chapters…
                  </div>
                ) : (
                  <PreviewLock hidden={previewHidden} onUnlock={onUnlock} />
                )
              )}
            </div>
          ) : (
            <div className="ws-state-center">
              <p className="ws-state-text">Timestamps unavailable.</p>
            </div>
          )
        )}
      </>
    );
  }

  return null;
};

// The rest of a free preview's chapters: blurred placeholder rows and the way to unlock them
const PreviewLock = ({ hidden, onUnlock }) => (
  <div className="ws-preview-lock">
    <div className="ws-preview-ghosts" aria-hidden="true">
      {Array.from({ length: Math.min(Math.max(hidden, 2), 4) }, (_, i) => (
        <div key={i} className="ws-ts-row">
          <span className="ws-ts-time">{`${12 + i * 7}:${['05', '40', '15', '50'][i]}`}</span>
          <span className="ws-ts-sep">—</span>
          <span className="ws-ts-text">{['Chapter hidden in the preview', 'Another moment worth finding', 'More of the video', 'And the rest of it'][i]}</span>
        </div>
      ))}
    </div>
    <div className="ws-preview-cta">
      <p className="ws-preview-title">
        <FontAwesomeIcon icon={faLock} /> {hidden > 0 ? `${hidden} more chapter${hidden === 1 ? '' : 's'} in this video` : 'Free preview'}
      </p>
      <p className="ws-preview-sub">Your free preview shows the first chapters. Pro unlocks all of them, plus 25 chat messages per video.</p>
      <button type="button" className="ws-preview-btn" onClick={onUnlock}>Unlock with Pro</button>
    </div>
  </div>
);

const PrivateVideoBanner = ({ onRetry, loading }) => (
  <div className="ws-private-banner">
    <p className="ws-private-banner-text">
      <strong>Video is Private or Unlisted.</strong> AI processing requires your video to be <strong>Public</strong> on YouTube. Once you've updated the visibility, click Retry.
    </p>
    <button className="ws-retry-btn" onClick={onRetry} disabled={loading}>
      <FontAwesomeIcon icon={faRotateRight} className="ws-retry-icon" />
      {loading ? 'Generating...' : 'Retry'}
    </button>
  </div>
);

const RetryBtn = ({ onClick, loading }) => (
  <button className="ws-retry-btn" onClick={onClick} disabled={loading}>
    <FontAwesomeIcon icon={faRotateRight} className="ws-retry-icon" />
    {loading ? 'Generating...' : 'Retry'}
  </button>
);

export default WorkspaceShortcutsPanel;
